import dynamic from 'next/dynamic';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useBreakpoint from 'hooks/useBreakpoint';

// Interfaces
import type { FranchiseListSearch } from 'interfaces/content/Ranking';

// Utils
import { UIFragments } from 'utils/log/constants/uiFragments';
import { AppErrorSeverity } from 'utils/log/constants/errorSeverity';
import { changeRoute } from 'components/Globals/Base/Link';

// Components
import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import { Title } from './RankingSearchPageStyles';
import * as S from './RankingSearchPageStyles';

const RankingSearchTable = dynamic(() => import('components/Ranking/SearchTable'));
const Filters = dynamic(() => import('components/Ranking/Filters'));
const Placeholder = dynamic(() => import('components/Placeholder'));

export interface RankingsProps {
  franchiseList: FranchiseListSearch;
  initialFilters: { [key: string]: string | boolean };
  years: string[];
  initialYear: string;
  initialSearch: string;
  showTitle?: boolean;
  initialRowCount?: number;
  rankingSlug?: string;
}

const RankingSearchPage: FC<RankingsProps> = ({
  franchiseList,
  initialFilters,
  years,
  initialYear,
  initialSearch,
  showTitle = true,
  initialRowCount = 10,
  rankingSlug,
}) => {
  const router = useRouter();
  const {
    items: initialItems,
    sortableFields: initialSortableFields,
    searchFields: initialSearchFields,
  } = franchiseList;

  const [currentYearItems, setCurrentYearItems] = useState(initialItems);
  const [items, setItems] = useState(initialItems);
  const [sortableFields, setSortableFields] = useState(initialSortableFields);
  const [searchFields, setSearchFields] = useState(initialSearchFields);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [rowCount, setRowCount] = useState(initialRowCount);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [search, setSearch] = useState(initialSearch);
  const [year, setYear] = useState(initialYear);
  const isSearchPage = router.route.split('/').includes('search');
  const [currentFranchiseList, setCurrentFranchiseList] = useState(franchiseList);

  const filters = useMemo(() => {
    if (!searchFields) return [];
    const filterFields = searchFields?.map((field) => {
      const { fieldName } = field;
      return fieldName;
    });
    return filterFields;
  }, [searchFields]);

  const isMobile = useBreakpoint('mdMax');

  const updateQueryParam = (type: 'ADD' | 'DELETE', name: string, value: string | null) => {
    if (type === 'DELETE') {
      delete router.query[name];
    } else {
      router.query[name] = value as string;
    }
    // eslint-disable-next-line no-void
    void router.push(router, undefined, { shallow: true });
  };

  const resetQueryParam = () => {
    // Remove all filters from the URL, keep other query params
    const paths = router.asPath.split('?');
    const queryString = new URLSearchParams(paths[1] || '');
    const path = paths[0];
    if (!filters) return;
    filters?.forEach((filter) => {
      if (queryString.has(filter)) {
        queryString.delete(filter);
      }
    });
    const query = queryString.toString();
    const newPath = `${path}${query ? `?${query}` : ''}`;

    // eslint-disable-next-line no-void
    void router.push(newPath, undefined, { shallow: true });
  };

  const updateFilters = (type: 'ADD' | 'DELETE', name: string, value: string | boolean) => {
    setActiveFilters((prevActive) => {
      if (type === 'ADD') {
        return { ...prevActive, [name]: value };
      }
      // eslint-disable-next-line no-param-reassign
      delete prevActive[name];
      return { ...prevActive };
    });
    updateQueryParam(type, name, value as string);
  };

  const resetFilters = () => {
    setActiveFilters({});
    setSearch('');
    resetQueryParam();
  };

  const updateSearch = (value: string) => {
    setSearch(value);
    updateQueryParam(value === '' ? 'DELETE' : 'ADD', 'Name', value);
  };

  const updateYear = async (value: string) => {
    if (isSearchPage) {
      await changeRoute(`/ranking/${franchiseList.slug}/${value}/search/`)
        .then((res) => {
          setYear(value);
          setSearch('');
          setActiveFilters({});
          return res;
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      setYear(value);

      return true;
    }
    return false;
  };

  const updateRowCount = (count: number) => {
    setRowCount(count);
  };

  useEffect(() => {
    const activeFilterNames = Object.keys(activeFilters);
    const filteredItems = currentYearItems?.filter((item) => {
      let shouldDisplay = true;
      activeFilterNames.forEach((activeFilterFieldName) => {
        const activeFilterValue =
          typeof activeFilters[activeFilterFieldName] === 'string' ? activeFilters[activeFilterFieldName] : 'yes';
        // eslint-disable-next-line max-len
        const activeFilterIndex = searchFields.findIndex((field) => field.fieldName === activeFilterFieldName);
        if (activeFilterIndex === -1) {
          shouldDisplay = false;
        } else {
          const activeFilterName = searchFields[activeFilterIndex].title;
          if (item.data[activeFilterName] !== activeFilterValue) shouldDisplay = false;
        }
      });
      if (!item.name.toLowerCase().includes(search.toLowerCase())) {
        shouldDisplay = false;
      }
      return shouldDisplay;
    });
    const sortedFilteredItems = [...filteredItems].sort((itemA, itemB) => {
      const searchLower = search.toLowerCase();
      const itemAName = itemA.name.toLowerCase();
      const itemBName = itemB.name.toLowerCase();

      const itemAStarts = itemAName.startsWith(searchLower);
      const itemBStarts = itemBName.startsWith(searchLower);

      if (itemAStarts && !itemBStarts) return -1;
      if (!itemAStarts && itemBStarts) return 1;

      if (itemA.rank !== itemB.rank) {
        return itemA.rank - itemB.rank;
      }

      return itemA.name.localeCompare(itemB.name);
    });

    setItems(sortedFilteredItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, search]);

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setActiveFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    setItems(currentFranchiseList.items);
    setSortableFields(currentFranchiseList.sortableFields);
    setSearchFields(currentFranchiseList.searchFields);
  }, [franchiseList, currentFranchiseList]);

  useEffect(() => {
    if (year === currentFranchiseList.year) {
      // The query string is completely ignored on reload,
      // and the filters are reset (see below)
      // Why are we doing this? If we don't want to persist the filter state,
      // we might as well just not use the query string at all
      resetFilters();
      return;
    }

    if (!isSearchPage) {
      setShowSkeleton(true);
      const getNewSearchYear = () => {
        const getNewSearchYearAsync = async () => {
          try {
            const response = await fetch(`/api/getRankingSearchYear/${franchiseList.slug}/${year}`);
            if (response.ok) {
              const updatedList = (await response.json()) as FranchiseListSearch;
              setCurrentFranchiseList(updatedList);
              setCurrentYearItems(updatedList.items);
              resetFilters();
            }
          } catch (error) {
            console.error(error);
          }
          setShowSkeleton(false);
        };
        getNewSearchYearAsync().catch(console.error);
      };

      setTimeout(() => getNewSearchYear());
    }
  }, [year, currentFranchiseList]);

  return (
    <S.Wrapper className='franchise-content'>
      <ErrorBoundary fragment={UIFragments.RANKING_SEARCH_PAGE}>
        {showTitle && (
          <Title
            as='h1'
            $fontFamily='graphikCond'
            $size={{ default: 'level2', lgMin: 'xlarge', mdMin: 'large' }}
          >
            {currentFranchiseList.title}
          </Title>
        )}
      </ErrorBoundary>
      <ErrorBoundary
        fragment={UIFragments.RANKING_SEARCH_PAGE}
        severity={AppErrorSeverity.HIGH}
      >
        <Filters
          filters={searchFields}
          years={years}
          activeFilters={activeFilters}
          activeYear={year}
          search={search}
          updateFilters={updateFilters}
          resetFilters={resetFilters}
          updateSearch={updateSearch}
          updateYear={updateYear}
          showSkeleton={showSkeleton}
          isMobile={isMobile}
        />
      </ErrorBoundary>
      <ErrorBoundary
        fragment={UIFragments.RANKING_SEARCH_PAGE}
        severity={AppErrorSeverity.HIGH}
      >
        <S.TableWrapper>
          {!showSkeleton &&
            (items?.length > 0 ? (
              <RankingSearchTable
                items={items}
                sortableFields={sortableFields}
                initialRowCount={rowCount}
                updateRowCount={updateRowCount}
                rankingSlug={rankingSlug}
                search={search}
              />
            ) : (
              <S.ResultText data-cy='no-results'>
                No results found matching your search criteria. Please search again.
              </S.ResultText>
            ))}
          {showSkeleton && <Placeholder size={{ height: '100%' }} />}
        </S.TableWrapper>
      </ErrorBoundary>
    </S.Wrapper>
  );
};

export default RankingSearchPage;
